/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import {
    Button, Select, Table, Tag, Alert, message, Tooltip, Switch,
    Modal, Progress, Spin, Card, Steps, Statistic,
} from 'antd';
import {
    CheckCircleOutlined, CloseCircleOutlined, WarningOutlined,
    ArrowRightOutlined, FileExcelOutlined,
} from '@ant-design/icons';
import * as ExcelJS from 'exceljs';
import QoCService from '../../services/QoCService';
import FacilityService from '../../services/FacilityService';
import { trailCreate, TRAIL_MODULES } from '../../utils/activityTrail';
const { Option } = Select;
const { Step }   = Steps;

// ─── Score mapping ─────────────────────────────────────────────────────────────
const SCORE_MAP = {
    'met':            2,
    'partially met':  1,
    'not met':        0,
    'not applicable': -1,
    'not checked':    -2,
};
const scoreValue = raw => {
    if (raw === null || raw === undefined || raw === '') return -1;
    return SCORE_MAP[String(raw).trim().toLowerCase()] ?? -1;
};

// ─── Parse KoboCollect multi-facility Excel ────────────────────────────────────
// Returns { facilities[], questionCols[] }
async function parseKoboFile(file) {
    const buf = await file.arrayBuffer();
    const wb  = new ExcelJS.Workbook();
    await wb.xlsx.load(buf);
    const ws = wb.worksheets[0];

    const headers = [];
    ws.getRow(1).eachCell((cell, colNum) => {
        headers[colNum - 1] = String(cell.value || '').trim();
    });

    // Identify ME question columns:
    // Each question block = [Q text, General Observation, Criticality, Areas of good Practice]
    const SKIP_HEADERS = new Set([
        'start','end','Region','Facility name','Facility Name','Type of facility ',
        'Scope of Service','Scope of Service/Preventative ','Scope of Service/Curative',
        'Scope of Service/Child Welfare','License Number','Issue date','Expiry Date',
        'Facility Address','_Facility Address_latitude','_Facility Address_longitude',
        '_Facility Address_altitude','_Facility Address_precision',
        'Type of Audit','Audit Date','Auditor Name',
        'General Observation','Criticality of Observation','Areas of good Practice',
        'Documented SOPs','Screening forms','Admission form','Transfer forms',
        'Discharge forms','Scope of Audit','Issue Date',
        '_id','_uuid','_submission_time','_validation_status','_notes',
        '_status','_submitted_by','__version__','_tags','_index',
    ]);

    const questionCols = [];
    let i = 0;
    while (i < headers.length) {
        const h    = headers[i];
        const next = headers[i + 1] || '';
        if (h && !SKIP_HEADERS.has(h) && next === 'General Observation') {
            const numMatch = h.match(/^(\d+[\d.]*)/);
            const code     = numMatch ? numMatch[1].replace(/\.$/, '') : String(questionCols.length + 1);
            questionCols.push({ index: i, text: h, code });
            i += 4;
            continue;
        }
        i++;
    }

    // Facility name columns: KoboCollect spreads across cols 4-7 (0-indexed 3-6)
    const FAC_COLS = [3, 4, 5, 6];

    const facilities = [];
    ws.eachRow((row, rowNum) => {
        if (rowNum === 1) return;
        const vals = [];
        row.eachCell({ includeEmpty: true }, (cell, colNum) => {
            vals[colNum - 1] = cell.value;
        });

        const facilityName = FAC_COLS.map(idx => vals[idx])
            .find(v => v && String(v).trim()) || '';
        if (!facilityName) return;

        const rawRegion = String(vals[2] || '').trim();
        const region    = rawRegion.charAt(0).toUpperCase() + rawRegion.slice(1).toLowerCase();

        const auditDateRaw = vals[21];
        const auditDate    = auditDateRaw instanceof Date
            ? auditDateRaw.toISOString().split('T')[0]
            : String(auditDateRaw || '').split('T')[0];

        const responses = questionCols.map(q => ({
            question_code: q.code,
            question_text: q.text,
            score_raw:     vals[q.index]  ? String(vals[q.index]).trim()      : '',
            score:         scoreValue(vals[q.index]),
            observation:   vals[q.index + 1] ? String(vals[q.index + 1]).trim() : '',
            criticality:   vals[q.index + 2] ? String(vals[q.index + 2]).trim() : '',
            good_practice: vals[q.index + 3] ? String(vals[q.index + 3]).trim() : '',
        }));

        facilities.push({
            facilityName: String(facilityName).trim(),
            region,
            facilityType: String(vals[7] || '').trim(),
            auditDate,
            auditor: String(vals[22] || '').trim(),
            responses,
        });
    });

    return { facilities, questionCols };
}

// ─── Fuzzy match facility name → existing platform facility ──────────────────
function autoMatch(name, list) {
    const norm = s => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const n    = norm(name);
    let match  = list.find(f => norm(f.business_name) === n);
    if (match) return { facility: match, confidence: 'exact' };
    match      = list.find(f => norm(f.business_name).includes(n) || n.includes(norm(f.business_name)));
    if (match) return { facility: match, confidence: 'partial' };
    return null;
}

// ─────────────────────────────────────────────────────────────────────────────
export default function QoCAssessmentImport() {

    const [mode, setMode] = useState('standard'); // 'standard' | 'kobo'

    // Shared
    const [facilities,        setFacilities]        = useState([]);
    const [loadingFacilities, setLoadingFacilities]  = useState(false);

    // Standard import
    const [selectedFacility, setSelectedFacility] = useState(null);
    const [migrationMode,    setMigrationMode]    = useState(false);
    const [periods,          setPeriods]          = useState([]);
    const [selectedPeriod,   setSelectedPeriod]   = useState(null); // period object
    const [stdStep,          setStdStep]           = useState(0);
    const [parsedData,       setParsedData]        = useState(null);
    const [saveResults,      setSaveResults]       = useState(null);

    // Kobo import
    const [clauses,       setClauses]       = useState([]);
    const [koboClause,    setKoboClause]    = useState(null); // selected clause code for KoboCollect import
    const [koboStep,      setKoboStep]      = useState(0);
    const [koboRows,      setKoboRows]      = useState([]);
    const [koboQCols,     setKoboQCols]     = useState([]);
    const [koboProgress,  setKoboProgress]  = useState(0);
    const [koboSaving,    setKoboSaving]    = useState(false);
    const koboAbortRef = useRef(false);

    useEffect(() => {
        loadFacilities();
    }, []);

    const loadFacilities = () => {
        setLoadingFacilities(true);
        Promise.all([
            FacilityService.all({ status: 1 }),
            QoCService.getPeriods(),
            QoCService.getClauses(),
        ]).then(([fRes, pRes, cRes]) => {
            setFacilities(fRes.data?.result?.data || []);
            const pList = pRes.data?.data || [];
            setPeriods(pList);
            // Auto-select the current period
            const current = pList.find(p => parseInt(p.is_current) === 1) || pList[0] || null;
            if (current) setSelectedPeriod(current);
            const cList = cRes.data?.data || [];
            setClauses(cList);
            // Default KoboCollect clause to AACC (or first clause)
            const aacc = cList.find(c => c.code === 'AACC') || cList[0] || null;
            if (aacc) setKoboClause(aacc.code);
        }).catch(() => {}).finally(() => setLoadingFacilities(false));
    };

    // ── Add new facility ──────────────────────────────────────────────────────


    // ─────────────────────────────────────────────────────────────────────────
    //  STANDARD IMPORT
    // ─────────────────────────────────────────────────────────────────────────
    const handleStandardFile = async file => {
        if (!selectedFacility) { message.error('Please select a facility first'); return false; }
        try {
            const buf = await file.arrayBuffer();
            const wb  = new ExcelJS.Workbook();
            await wb.xlsx.load(buf);
            const ws = wb.worksheets[0];
            const headers = [];
            ws.getRow(1).eachCell((cell, colNum) => { headers[colNum - 1] = String(cell.value || '').trim(); });
            const clauseCode = String(ws.getRow(2).getCell(1).value || 'AACC').trim();
            const rows = [];
            ws.eachRow((row, rowNum) => {
                if (rowNum <= 2) return;
                const r = {};
                row.eachCell({ includeEmpty: true }, (cell, colNum) => { r[headers[colNum - 1]] = cell.value; });
                if (Object.values(r).some(v => v !== null && v !== '')) rows.push(r);
            });
            setParsedData({ clauseCode, rows });
            setStdStep(1);
        } catch (e) { message.error('Error reading file: ' + e.message); }
        return false;
    };

    const handleStandardSave = async () => {
        if (!parsedData || !selectedFacility) return;
        setStdStep(2);
        const results = [];
        for (const row of parsedData.rows) {
            try {
                let aId;
                if (migrationMode) {
                    const r = await QoCService.saveMigratedAssessment({
                        facility_id: selectedFacility,
                        assessment_date: row.audit_date || (selectedPeriod?.period_start || new Date().toISOString().split('T')[0]),
                        period_id:       selectedPeriod?.id   || null,
                        period_label:    selectedPeriod?.label || '',
                        clause_code: parsedData.clauseCode,
                        audit_type: 'migrated',
                    });
                    aId = r.data?.data?.id || r.data?.id;
                } else {
                    const r = await QoCService.saveAssessmentForSchedule({
                        facility_id: selectedFacility,
                        clause_code: parsedData.clauseCode,
                    });
                    aId = r.data?.data?.id || r.data?.id;
                }
                if (!aId) throw new Error('No assessment ID returned');
                const responses = Object.entries(row)
                    .filter(([k]) => !['audit_date', 'auditor', 'facility'].includes(k))
                    .map(([k, v]) => ({ question_code: k, score: scoreValue(v), score_label: v ? String(v) : '' }));
                await QoCService.saveAssessmentResponsesME(aId, responses);
                results.push({ code: parsedData.clauseCode, status: 'saved', migrated: migrationMode });
            } catch (e) {
                results.push({ code: parsedData.clauseCode, status: 'error', msg: e.message });
            }
        }

        setSaveResults(results);
        const savedCount = results.filter(r => r.status === 'saved').length;
        const facilityName = facilities.find(f => f.id === selectedFacility)?.business_name || String(selectedFacility);
        trailCreate(TRAIL_MODULES.ASSESSMENT_IMPORT, `${migrationMode ? 'Historic' : 'Standard'} Import — ${facilityName}`, '',
            { clause: parsedData.clauseCode, saved: savedCount, failed: results.length - savedCount,
              mode: migrationMode ? 'historic' : 'standard', period: selectedPeriod?.label });
        setStdStep(3);
    };

    // ─────────────────────────────────────────────────────────────────────────
    //  KOBO IMPORT
    // ─────────────────────────────────────────────────────────────────────────
    const handleKoboFile = async file => {
        try {
            message.loading({ content: 'Parsing KoboCollect file…', key: 'kp', duration: 0 });
            const { facilities: parsed, questionCols } = await parseKoboFile(file);
            if (!parsed.length) { message.error({ content: 'No facility data found', key: 'kp' }); return false; }

            setKoboQCols(questionCols);
            const mapped = parsed.map((row, idx) => {
                const m = autoMatch(row.facilityName, facilities);
                return {
                    ...row,
                    key:             idx,
                    matchedFacility: m?.facility  || null,
                    matchConfidence: m?.confidence || null,
                    facilityId:      m?.facility?.id || null,
                    _status:         'pending',
                    _msg:            '',
                };
            });
            setKoboRows(mapped);
            setKoboStep(1);
            message.success({ content: `${parsed.length} facilities parsed`, key: 'kp' });
        } catch (e) {
            message.error({ content: 'Parse error: ' + e.message, key: 'kp' });
        }
        return false;
    };

    const updateKoboRowFacility = (idx, facilityId) => {
        setKoboRows(prev => prev.map(r => {
            if (r.key !== idx) return r;
            const fac = facilities.find(f => f.id === facilityId) || null;
            return { ...r, facilityId, matchedFacility: fac };
        }));
    };

    const handleKoboSave = async () => {
        const toSave = koboRows.filter(r => r._status !== 'saved' && r.facilityId);
        if (!toSave.length) { message.warning('No mapped facilities to save'); return; }
        setKoboSaving(true); setKoboStep(2); setKoboProgress(0); koboAbortRef.current = false;

        // Map KoboCollect score labels → fulfillment values used by qoc_assessment_responses
        const toFulfillment = raw => {
            const s = String(raw || '').trim().toLowerCase();
            if (s === 'met')            return 'Met';
            if (s === 'partially met')  return 'Partially Met';
            if (s === 'not met')        return 'Not Met';
            if (s === 'not applicable' || s === 'na') return 'Not Applicable';
            return '';
        };

        // Fetch MEs for the selected clause, to resolve question_code (ref_no) → me_id
        const importClauseCode = koboClause || 'AACC';
        let meMap = {};
        try {
            const meRes = await QoCService.getMeasurableElements({ clause_code: importClauseCode });
            const meList = meRes.data?.data || meRes.data || [];
            meList.forEach(me => { if (me.ref_no) meMap[me.ref_no.trim()] = me.id; });
        } catch (e) { /* if MEs not available, me_id will be null and backend skips */ }

        let done = 0;
        for (const row of toSave) {
            if (koboAbortRef.current) break;
            try {
                const aRes = await QoCService.saveMigratedAssessment({
                    facility_id:     row.facilityId,
                    assessment_date: row.auditDate,
                    assessor_name:   row.auditor,
                    audit_type:      'migrated',
                    clause_code:     importClauseCode,
                });
                const aId = aRes.data?.data?.id || aRes.data?.id;
                if (!aId) throw new Error('No assessment ID returned');

                const clauseCode = importClauseCode;
                const responses = row.responses
                    .filter(r => r.score_raw && toFulfillment(r.score_raw))
                    .map(r => {
                        // DB ref_no is "AACC.1.1" but KoboCollect code is "1.1" — prefix it
                        const fullRef = `${clauseCode}.${r.question_code}`;
                        return {
                            me_id:                      meMap[fullRef] || meMap[r.question_code] || null,
                            ref_no:                     fullRef,
                            fulfillment:                toFulfillment(r.score_raw),
                            comment:                    r.observation   || '',
                            general_observation:        r.observation   || '',
                            criticality_of_observation: r.criticality   || '',
                            area_of_good_practice:      r.good_practice || '',
                            evidence_notes:             '',
                        };
                    })
                    .filter(r => r.me_id);  // only save rows where me_id resolved
                if (responses.length) {
                    await QoCService.saveAssessmentResponsesME(aId, responses);
                }
                setKoboRows(prev => prev.map(r => r.key === row.key ? { ...r, _status: 'saved', _msg: `#${aId}` } : r));
            } catch (e) {
                const msg = e?.response?.data?.message || e.message || 'Error';
                setKoboRows(prev => prev.map(r => r.key === row.key ? { ...r, _status: 'error', _msg: msg } : r));
            }
            done++;
            setKoboProgress(Math.round((done / toSave.length) * 100));
        }

        setKoboSaving(false);
        const finalSaved  = koboRows.filter(r => r._status === 'saved').length + done - koboRows.filter(r => r._status === 'error').length;
        const finalFailed = koboRows.filter(r => r._status === 'error').length;
        trailCreate(TRAIL_MODULES.ASSESSMENT_IMPORT, `KoboCollect Import — ${importClauseCode}`, '',
            { clause: importClauseCode, facilityCount: toSave.length,
              saved: done, failed: finalFailed, meCount: koboQCols.length });
        setKoboStep(3);
    };

    // Derived counts
    const koboMapped   = koboRows.filter(r => r.facilityId).length;
    const koboUnmapped = koboRows.filter(r => !r.facilityId).length;
    const koboSaved    = koboRows.filter(r => r._status === 'saved').length;
    const koboFailed   = koboRows.filter(r => r._status === 'error').length;

    // ── Kobo review table columns ──────────────────────────────────────────
    const koboColumns = [
        { title: '#', width: 42, render: (_, __, i) => <span style={{ color: '#aaa', fontSize: 12 }}>{i + 1}</span> },
        {
            title: 'KoboCollect Facility',
            width: 220,
            render: (_, row) => (
                <div>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>{row.facilityName}</div>
                    <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                        {row.region} · {row.auditDate}
                    </div>
                    <div style={{ fontSize: 11, color: '#aaa' }}>Auditor: {row.auditor || '—'}</div>
                </div>
            ),
        },
        {
            title: (
                <span>
                    Map to Platform Facility
                    <Tooltip title="Auto-match by name. Review partial matches carefully.">
                        <span style={{ marginLeft: 4, color: '#1890ff', cursor: 'help', fontSize: 11 }}>ⓘ</span>
                    </Tooltip>
                </span>
            ),
            render: (_, row) => {
                if (row._status === 'saved') {
                    return <span style={{ color: '#52c41a', fontWeight: 600 }}>{row.matchedFacility?.business_name}</span>;
                }
                return (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <Select
                            style={{ flex: 1, minWidth: 0 }}
                            size="small"
                            showSearch
                            allowClear
                            value={row.facilityId || undefined}
                            placeholder="Search…"
                            filterOption={(input, opt) =>
                                String(opt.children || '').toLowerCase().includes(input.toLowerCase())
                            }
                            onChange={v => updateKoboRowFacility(row.key, v)}
                        >
                            {facilities.map(f => (
                                <Option key={f.id} value={f.id}>
                                    {f.business_name}{f.located_region ? ` (${f.located_region})` : ''}
                                </Option>
                            ))}
                        </Select>
                        {row.matchConfidence === 'exact' && (
                            <Tag color="success" style={{ margin: 0, fontSize: 10, flexShrink: 0 }}>✓ Exact</Tag>
                        )}
                        {row.matchConfidence === 'partial' && (
                            <Tooltip title="Partial match — confirm before saving">
                                <Tag color="warning" style={{ margin: 0, fontSize: 10, flexShrink: 0 }}>~ Partial</Tag>
                            </Tooltip>
                        )}

                    </div>
                );
            },
        },
        {
            title: 'MEs',
            width: 75,
            render: (_, row) => {
                const filled = row.responses.filter(r => r.score_raw).length;
                const total  = row.responses.length;
                return (
                    <Tooltip title={`${filled}/${total} MEs have a score`}>
                        <Tag color={filled > 0 ? 'blue' : 'default'} style={{ fontSize: 11 }}>
                            {filled}/{total}
                        </Tag>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Status',
            width: 115,
            render: (_, row) => {
                if (row._status === 'saved')  return <Tag color="success" icon={<CheckCircleOutlined />}>Saved</Tag>;
                if (row._status === 'error')  return <Tooltip title={row._msg}><Tag color="error" icon={<CloseCircleOutlined />}>Failed</Tag></Tooltip>;
                if (!row.facilityId)          return <Tag color="warning" icon={<WarningOutlined />}>Unmapped</Tag>;
                return <Tag color="blue">Ready</Tag>;
            },
        },
        {
            title: '',
            width: 38,
            render: (_, row) => row._status !== 'saved'
                ? <Button size="small" type="text" danger icon={<CloseCircleOutlined />} onClick={() => setKoboRows(prev => prev.filter(r => r.key !== row.key))} />
                : null,
        },
    ];

    // ─────────────────────────────────────────────────────────────────────────
    //  RENDER
    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div className="page-content">
            {/* Header with mode toggle */}
            <div className="page-head uc d-flex" style={{ alignItems: 'center' }}>
                <div className="my-auto"><h2>Assessment Import</h2></div>
                <div className="ml-auto my-auto" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 12, color: '#888' }}>Mode:</span>
                    <div style={{ display: 'flex', background: '#f0f0f0', borderRadius: 8, padding: 3, gap: 2 }}>
                        {[
                            { key: 'standard', label: 'Standard (Single Facility)', color: '#1890ff' },
                            { key: 'kobo',     label: 'KoboCollect (Multi-Facility)', color: '#722ed1' },
                        ].map(m => (
                            <button key={m.key} onClick={() => {
                                setMode(m.key);
                                setStdStep(0); setParsedData(null); setSaveResults(null);
                                setKoboStep(0); setKoboRows([]);
                            }} style={{
                                padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
                                fontSize: 12, fontWeight: 600, transition: 'all 0.15s',
                                background: mode === m.key ? m.color : 'transparent',
                                color: mode === m.key ? '#fff' : '#666',
                            }}>{m.label}</button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="page-pad">

                {/* ═══════════════════════════════════════
                    STANDARD IMPORT
                ═══════════════════════════════════════ */}
                {mode === 'standard' && (
                    <>
                        <Steps current={stdStep} size="small" style={{ marginBottom: 24 }}>
                            <Step title="Select Facility" />
                            <Step title="Upload & Review" />
                            <Step title="Saving" />
                            <Step title="Done" />
                        </Steps>

                        {stdStep === 0 && (
                            <Card size="small" title="Select Facility">
                                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                    <div style={{ flex: 1, minWidth: 260 }}>
                                        <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Facility</div>
                                        <div style={{ display: 'flex', gap: 6 }}>
                                            <Select
                                                showSearch style={{ flex: 1 }} placeholder="Search facility…"
                                                value={selectedFacility || undefined}
                                                loading={loadingFacilities}
                                                filterOption={(i, o) => String(o.children || '').toLowerCase().includes(i.toLowerCase())}
                                                onChange={v => setSelectedFacility(v)}
                                            >
                                                {facilities.map(f => <Option key={f.id} value={f.id}>{f.business_name}</Option>)}
                                            </Select>

                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Mode</div>
                                        <Switch checked={migrationMode} onChange={setMigrationMode}
                                            checkedChildren="Historic" unCheckedChildren="Standard" />
                                    </div>
                                    {migrationMode && (
                                        <div>
                                            <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Period</div>
                                            <Select
                                                value={selectedPeriod?.id || undefined}
                                                onChange={v => setSelectedPeriod(periods.find(p => p.id === v) || null)}
                                                style={{ minWidth: 160 }}
                                                placeholder="Select period"
                                            >
                                                {periods.map(p => (
                                                    <Option key={p.id} value={p.id}>
                                                        {p.label}
                                                        {parseInt(p.is_current) === 1 ? ' ✓' : ''}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </div>
                                    )}
                                    <Button type="primary" disabled={!selectedFacility} onClick={() => setStdStep(1)}>
                                        Next <ArrowRightOutlined />
                                    </Button>
                                </div>
                                {migrationMode && (
                                    <Alert type="info" showIcon style={{ marginTop: 12 }}
                                        message={`Historic data — assessments saved with status "Migrated", no schedule required. Period: ${selectedPeriod?.label || '—'} (${selectedPeriod?.period_start || ''} → ${selectedPeriod?.period_end || ''})`} />
                                )}
                            </Card>
                        )}

                        {stdStep === 1 && (
                            <Card size="small"
                                title={`Upload Excel — ${facilities.find(f => f.id === selectedFacility)?.business_name || ''}`}
                                extra={<Button size="small" onClick={() => setStdStep(0)}>← Back</Button>}
                            >
                                <p style={{ color: '#555', fontSize: 13 }}>
                                    Upload the completed Excel assessment file. Scores: Met / Partially Met / Not Met / Not Applicable.
                                </p>
                                <input type="file" accept=".xlsx,.xls"
                                    onChange={e => { if (e.target.files[0]) handleStandardFile(e.target.files[0]); }} />
                                {parsedData && (
                                    <div style={{ marginTop: 12 }}>
                                        <Alert type="success" showIcon style={{ marginBottom: 12 }}
                                            message={`Clause ${parsedData.clauseCode} — ${parsedData.rows.length} row(s) ready`} />
                                        <Button type="primary" onClick={handleStandardSave}>Save</Button>
                                    </div>
                                )}
                            </Card>
                        )}

                        {stdStep === 2 && (
                            <Card size="small" title="Saving…">
                                <Spin tip="Saving assessments…" size="large"><div style={{ padding: 40 }} /></Spin>
                            </Card>
                        )}

                        {stdStep === 3 && saveResults && (
                            <Card size="small"
                                title={saveResults.some(r => r.migrated) ? 'Migration Complete' : 'Import Complete'}
                                extra={<Button onClick={() => { setStdStep(0); setParsedData(null); setSaveResults(null); }}>New Import</Button>}
                            >
                                <Alert type="success" showIcon style={{ marginBottom: 12 }}
                                    message={`${saveResults.length} assessment(s) saved`} />
                                <Table size="small" dataSource={saveResults} rowKey={(_, i) => i} pagination={false}
                                    columns={[
                                        { title: 'Clause', dataIndex: 'code' },
                                        { title: 'Status', render: (_, r) => r.status === 'saved' ? <Tag color="success">Saved</Tag> : <Tag color="error">Error</Tag> },
                                        { title: 'Note', dataIndex: 'msg' },
                                    ]} />
                            </Card>
                        )}
                    </>
                )}

                {/* ═══════════════════════════════════════
                    KOBO IMPORT
                ═══════════════════════════════════════ */}
                {mode === 'kobo' && (
                    <>
                        <Alert type="info" showIcon style={{ marginBottom: 16 }}
                            message="KoboCollect Multi-Facility Import"
                            description={`Upload a KoboCollect export. Each row = one facility. The system auto-matches facility names — review mismatches before saving. ${koboRows.length ? `${koboRows.length} facilities loaded.` : ''}`}
                        />

                        <Steps current={koboStep} size="small" style={{ marginBottom: 20 }}>
                            <Step title="Upload" />
                            <Step title="Review & Map" description={koboRows.length ? `${koboRows.length} facilities` : ''} />
                            <Step title="Saving" />
                            <Step title="Complete" />
                        </Steps>

                        {/* Step 0: Upload */}
                        {koboStep === 0 && (
                            <Card size="small" title={<span><FileExcelOutlined style={{ color: '#52c41a', marginRight: 6 }} />Upload KoboCollect Export</span>}>
                                <p style={{ color: '#555', fontSize: 13, marginBottom: 12 }}>
                                    Upload the KoboCollect .xlsx export file. One row per facility audited.
                                    The system will read all facilities, auto-match them to platform records,
                                    and extract all {koboQCols.length || ''} ME scores per facility.
                                </p>
                                <div style={{ marginBottom: 14 }}>
                                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, fontSize: 13 }}>
                                        Clause being assessed <span style={{ color: '#f5222d' }}>*</span>
                                    </label>
                                    <Select
                                        style={{ width: 340 }}
                                        placeholder="Select clause..."
                                        value={koboClause}
                                        onChange={setKoboClause}
                                        showSearch
                                        optionFilterProp="children"
                                    >
                                        {clauses.map(c => (
                                            <Select.Option key={c.code} value={c.code}>
                                                {c.code} — {c.title}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                    <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                                        Choose the clause your KoboCollect form assessed. Question codes will be prefixed with this clause.
                                    </div>
                                </div>
                                <input type="file" accept=".xlsx,.xls"
                                    onChange={e => { if (e.target.files[0]) handleKoboFile(e.target.files[0]); }} />
                            </Card>
                        )}

                        {/* Step 1: Review & Map */}
                        {koboStep === 1 && (
                            <>
                                {/* Summary cards */}
                                <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
                                    {[
                                        { title: 'Total Facilities', value: koboRows.length, color: '#1890ff' },
                                        { title: 'Auto-Matched',     value: koboRows.filter(r => r.matchConfidence === 'exact').length, color: '#52c41a' },
                                        { title: 'Partial Match',    value: koboRows.filter(r => r.matchConfidence === 'partial').length, color: '#faad14' },
                                        { title: 'Unmapped',         value: koboUnmapped, color: koboUnmapped > 0 ? '#f5222d' : '#52c41a' },
                                        { title: 'MEs / Facility',   value: koboQCols.length, color: '#722ed1' },
                                    ].map(s => (
                                        <Card key={s.title} size="small" style={{ flex: 1, minWidth: 100 }}
                                            bodyStyle={{ padding: '10px 14px' }}>
                                            <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>{s.title}</div>
                                            <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
                                        </Card>
                                    ))}
                                </div>

                                {koboUnmapped > 0 && (
                                    <Alert type="warning" showIcon style={{ marginBottom: 12 }}
                                        message={`${koboUnmapped} facilit${koboUnmapped > 1 ? 'ies' : 'y'} not matched — assign manually or click + to create. Unmapped rows will be skipped.`} />
                                )}

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                    <span style={{ fontSize: 13, color: '#555' }}>
                                        <strong>Clause:</strong>{' '}
                                        {clauses.find(c => c.code === koboClause)
                                            ? `${koboClause} — ${clauses.find(c => c.code === koboClause).title}`
                                            : <span style={{ color: '#f5222d' }}>No clause selected — go back to set one</span>
                                        }
                                    </span>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                    <Button onClick={() => { setKoboRows([]); setKoboStep(0); }}>Re-upload</Button>
                                    <Button
                                        disabled={koboMapped === 0}
                                        onClick={handleKoboSave}
                                        style={{ background: '#722ed1', borderColor: '#722ed1', color: '#fff' }}
                                        icon={<CheckCircleOutlined />}
                                    >
                                        Save {koboMapped} Assessment{koboMapped !== 1 ? 's' : ''}
                                    </Button>
                                    </div>{/* end inner button group */}
                                </div>{/* end flex-between */}

                                <Table
                                    dataSource={koboRows}
                                    columns={koboColumns}
                                    size="small"
                                    pagination={{ pageSize: 30, showSizeChanger: false, showTotal: (t) => `${t} facilities` }}
                                    rowClassName={row =>
                                        row._status === 'saved' ? 'kobo-saved' :
                                        row._status === 'error' ? 'kobo-error' :
                                        !row.facilityId         ? 'kobo-warn'  : ''
                                    }
                                    expandable={{
                                        rowExpandable: row => row.responses?.length > 0,
                                        expandedRowRender: row => (
                                            <div style={{ padding: '6px 8px 8px' }}>
                                                <div style={{ fontSize: 11, fontWeight: 700, color: '#888', marginBottom: 6 }}>
                                                    ME SCORES ({row.responses.filter(r => r.score_raw).length}/{row.responses.length} scored)
                                                </div>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                                                    {row.responses.map((r, idx) => {
                                                        const c = r.score === 2 ? 'success' : r.score === 1 ? 'warning' : r.score === 0 ? 'error' : 'default';
                                                        return (
                                                            <Tooltip key={idx} title={
                                                                <div style={{ fontSize: 11, maxWidth: 280 }}>
                                                                    <div style={{ fontWeight: 700, marginBottom: 3 }}>{r.question_text.substring(0, 100)}{r.question_text.length > 100 ? '…' : ''}</div>
                                                                    <div style={{ color: '#91d5ff' }}>Score: {r.score_raw || '—'}</div>
                                                                    {r.observation && <div>Obs: {r.observation}</div>}
                                                                    {r.criticality && <div>Crit: {r.criticality}</div>}
                                                                    {r.good_practice && <div>Good: {r.good_practice}</div>}
                                                                </div>
                                                            }>
                                                                <Tag color={c} style={{ fontSize: 10, cursor: 'default', margin: 1 }}>
                                                                    {r.question_code}
                                                                </Tag>
                                                            </Tooltip>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ),
                                    }}
                                />
                                <style>{`
                                    .kobo-saved td { background: #f6ffed !important; }
                                    .kobo-error td { background: #fff1f0 !important; }
                                    .kobo-warn  td { background: #fffbe6 !important; }
                                `}</style>
                            </>
                        )}

                        {/* Step 2: Saving */}
                        {koboStep === 2 && (
                            <Card size="small" title="Saving assessments…">
                                {koboSaving ? (
                                    <>
                                        <p style={{ color: '#555' }}>
                                            Saving {koboMapped} facility assessments ({koboQCols.length} MEs each)…
                                        </p>
                                        <Progress
                                            percent={koboProgress} status="active"
                                            format={p => `${p}% (${Math.round(p * koboMapped / 100)}/${koboMapped})`}
                                            style={{ marginBottom: 12 }}
                                        />
                                        <Button danger onClick={() => { koboAbortRef.current = true; }}>Stop</Button>
                                    </>
                                ) : <Spin size="large" />}
                            </Card>
                        )}

                        {/* Step 3: Done */}
                        {koboStep === 3 && (
                            <Card size="small"
                                title="Import Complete"
                                extra={<Button onClick={() => { setKoboRows([]); setKoboStep(0); }}>New Import</Button>}
                            >
                                <Alert
                                    type={koboFailed > 0 ? 'warning' : 'success'} showIcon style={{ marginBottom: 16 }}
                                    message={koboFailed > 0
                                        ? `${koboSaved} saved, ${koboFailed} failed`
                                        : `All ${koboSaved} assessments imported successfully`}
                                />
                                <Table
                                    dataSource={koboRows} size="small"
                                    pagination={{ pageSize: 30, showTotal: t => `${t} facilities` }}
                                    columns={[
                                        { title: 'KoboCollect Facility',  dataIndex: 'facilityName',  width: 200 },
                                        { title: 'Platform Facility',     render: (_, r) => r.matchedFacility?.business_name || '—', width: 200 },
                                        { title: 'Audit Date',            dataIndex: 'auditDate',     width: 110 },
                                        { title: 'Auditor',               dataIndex: 'auditor',       width: 160 },
                                        {
                                            title: 'Result', width: 150,
                                            render: (_, r) =>
                                                r._status === 'saved' ? <Tag color="success" icon={<CheckCircleOutlined />}>Saved {r._msg}</Tag> :
                                                r._status === 'error' ? <Tooltip title={r._msg}><Tag color="error" icon={<CloseCircleOutlined />}>Failed</Tag></Tooltip> :
                                                !r.facilityId         ? <Tag>Skipped — unmapped</Tag> :
                                                <Tag>Skipped</Tag>
                                        },
                                    ]}
                                />
                                {koboFailed > 0 && (
                                    <Button style={{ marginTop: 12 }} onClick={() => {
                                        setKoboRows(prev => prev.map(r => r._status === 'error' ? { ...r, _status: 'pending', _msg: '' } : r));
                                        setKoboStep(1);
                                    }}>
                                        Retry Failed
                                    </Button>
                                )}
                            </Card>
                        )}
                    </>
                )}
            </div>


        </div>
    );
}